import { config } from "../config";
import { callGateway, type CallerIdentity } from "./gateway";
import { SCOPE_FIELD } from "./reports";

/**
 * Per-NAS traffic from RADIUS accounting: radreportsapi POST /reports/traffic
 * (v0.1.27, 2026-09-15). The Welcome Page's infrastructure ledger.
 *
 * 🔑 What it can say: who is online now, sessions in the window, bytes in and
 * out, when each NAS was last heard from. What it cannot: a rate. radacct is
 * one row per session with cumulative counters and no time series, so
 * nothing here is labelled Mbps.
 */
export interface NASTraffic {
  namespace: string;
  nas_ip: string;
  nas_name: string;
  online_now: number;
  sessions: number;
  in_mb: number;
  out_mb: number;
  /** IST digits as stored, "YYYY-MM-DD HH:MM:SS". */
  last_update: string;
}

export interface Traffic {
  namespace_prefix: string;
  days: number;
  online_now: number;
  sessions: number;
  in_mb: number;
  out_mb: number;
  nas: NASTraffic[];
}

export async function fetchTraffic(identity: CallerIdentity, days = 1, signal?: AbortSignal): Promise<Traffic> {
  const r = await callGateway<Traffic>(identity, config().gatewayBase, "/radreport/reports/traffic", {
    method: "POST",
    body: { [SCOPE_FIELD]: identity.namespace, days },
    ...(signal ? { signal } : {}),
  });
  if (!r.data) throw new Error("The traffic report came back empty.");
  return { ...r.data, nas: r.data.nas ?? [] };
}

/** MB as a short human figure: 512 MB, 9.4 GB, 1.2 TB. */
export function formatMB(mb: number): string {
  if (mb >= 1024 * 1024) return `${(mb / 1024 / 1024).toFixed(1)} TB`;
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${Math.round(mb)} MB`;
}

export type NASStatus = "online" | "idle" | "quiet";

/**
 * online: someone is on it now. idle: accounting arrived in the window but
 * nobody is on. quiet: nothing in the window (the row exists only when there
 * was, so quiet means an older last_update than the window start).
 */
export function nasStatus(n: NASTraffic): NASStatus {
  if (n.online_now > 0) return "online";
  return n.sessions > 0 ? "idle" : "quiet";
}
